import { Check, Copy } from 'lucide-react'
import { useState } from 'react'
import { RoutingBarcode } from './RoutingBarcode'
import type { ForwardingOrder } from '../types'
import './ForwardingAddressCard.css'

export function ForwardingAddressCard({ order, message }: { order: ForwardingOrder; message: string }) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle')
  const address = order.forwardingAddress
  if (!address) return <section className="notice-panel" role="status">
    <h2>Your routing instructions need review.</h2>
    <p>Do not send a package yet. Refresh this order or contact <a href="mailto:support@rxrouting.com">support@rxrouting.com</a> with your order number.</p>
  </section>

  const cityLine = `${address.city}, ${address.subdivision} ${address.postalCode}`
  const country = address.countryCode === 'US' ? 'United States' : address.countryCode
  const text = [address.name, address.line1, address.line2, cityLine, country].join('\n')
  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(text)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('failed')
    }
  }

  return <section className="address-reveal address-reveal--order" aria-labelledby="forwarding-address-title">
    <h2 id="forwarding-address-title">Your U.S. forwarding address</h2>
    <p>{message || 'Your order is authorized.'} Use this address at the retailer’s checkout.</p>
    <div className="shipping-label forwarding-address-label">
      <div className="airmail-edge" />
      <div className="shipping-label__meta"><span>SHIP TO</span><b>RXID {order.rxid}</b></div>
      <address>
        <strong>{address.name}</strong>
        <span>{address.line1}</span>
        <span className="forwarding-address-label__routing">{address.line2}</span>
        <span>{cityLine}</span>
        <span>{country}</span>
      </address>
      <RoutingBarcode rxid={order.rxid} />
      <small>ONE PACKAGE · RXID {order.rxid}</small>
    </div>
    <button type="button" className="button button--primary button--large button--full" onClick={copyAddress}>
      {copyStatus === 'copied' ? <Check size={18} /> : <Copy size={18} />}
      {copyStatus === 'copied' ? 'Address copied' : 'Copy complete address'}
    </button>
    <p className={`forwarding-copy-status ${copyStatus === 'idle' ? 'sr-only' : ''}`} role="status">{copyStatus === 'failed' ? 'Could not copy automatically. Select and copy the address above.' : copyStatus === 'copied' ? 'Complete address copied, including your name and RXID.' : ''}</p>
    <div className="address-warning"><b>Keep the unit and RXID together on address line 2.</b><span>This RXID is for one package only. Start a new order for your next package.</span></div>
  </section>
}
